import { useState } from "react";
import type { NostrEvent } from "@nostrify/nostrify";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useAuthor } from "@/hooks/useAuthor";
import { useNostrPublish } from "@/hooks/useNostrPublish";
import { useGMContext } from "@/hooks/useGMContext";
import { useToast } from "@/hooks/useToast";
import { genUserName } from "@/lib/genUserName";
import { isValidGMMessage } from "@/lib/gmDetection";
import { buildReplyTags } from "@/lib/nip10";
import { NoteContent } from "@/components/NoteContent";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Sunrise, Loader2 } from "lucide-react";

/** Focused element pinned to the top of the feed: prompts the user to post
 * their daily GM, then tracks who has responded to it once posted. */
export function MyGMCard() {
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const { mutate: createEvent, isPending: isPublishing } =
    useNostrPublish();
  const { myGMNote, isLoadingMyGMNote, myGMReplies, userReplies, setMyGMNote } =
    useGMContext();

  const [message, setMessage] = useState("");
  const [isComposeOpen, setIsComposeOpen] = useState(false);

  if (!user) return null;

  const handlePublish = () => {
    if (!message.trim() || !isValidGMMessage(message)) {
      toast({
        title: "GM required!",
        description: 'Your message must include "GM" or "good morning".',
        variant: "destructive",
      });
      return;
    }

    createEvent(
      { kind: 1, content: message, tags: [] },
      {
        onSuccess: (event) => {
          setMyGMNote(event);
          toast({
            title: "GM published!",
            description: "Your GM has been sent to all your frens! ☀️",
          });
          setMessage("");
          setIsComposeOpen(false);
        },
        onError: () => {
          toast({
            title: "Failed to publish",
            description: "Something went wrong. Please try again.",
            variant: "destructive",
          });
        },
      },
    );
  };

  if (isLoadingMyGMNote) {
    return null;
  }

  // Not posted yet today - show the prompt
  if (!myGMNote) {
    return (
      <Dialog open={isComposeOpen} onOpenChange={setIsComposeOpen}>
        <DialogTrigger asChild>
          <Button className="w-full mb-6 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-lg py-6">
            <Sunrise className="mr-2 h-5 w-5" />
            You haven't GMed today — GM Your Frens ☀️
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish a GM Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="GM! ☀️"
              className="min-h-[120px]"
            />
            <p className="text-sm text-muted-foreground">
              Your message must include "GM" or "good morning"
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={handlePublish}
              disabled={
                !message.trim() || !isValidGMMessage(message) || isPublishing
              }
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
            >
              {isPublishing ? "Publishing..." : "Publish GM"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Already posted today - pin the note and track responses
  return (
    <Card className="mb-6 border-2 border-amber-300 dark:border-amber-800">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Sunrise className="h-4 w-4 text-amber-500" />
          <span className="text-sm font-semibold text-muted-foreground">
            Your GM today
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <NoteContent content={myGMNote.content} />

        <div className="border-t pt-4">
          {myGMReplies.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No one has responded yet. Check back soon! ☀️
            </p>
          ) : (
            <>
              <p className="text-sm font-medium text-muted-foreground mb-2">
                {myGMReplies.length}{" "}
                {myGMReplies.length === 1 ? "fren" : "frens"} responded
              </p>
              <div className="space-y-2">
                {myGMReplies.map((reply) => (
                  <ResponderRow
                    key={reply.id}
                    rootNote={myGMNote}
                    reply={reply}
                    hasRepliedBack={userReplies.some((r) =>
                      r.tags.some(
                        ([tag, value]) => tag === "e" && value === reply.id,
                      ),
                    )}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ResponderRow({
  rootNote,
  reply,
  hasRepliedBack,
}: {
  rootNote: NostrEvent;
  reply: NostrEvent;
  hasRepliedBack: boolean;
}) {
  const { toast } = useToast();
  const { mutate: createEvent, isPending } = useNostrPublish();
  const author = useAuthor(reply.pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? genUserName(reply.pubkey);
  const profileImage = metadata?.picture;

  const handleGMBack = () => {
    createEvent(
      {
        kind: 1,
        content: "GM",
        tags: buildReplyTags(rootNote, reply),
      },
      {
        onSuccess: () => {
          toast({
            title: "GM sent back!",
            description: `You GMed ${displayName} back ☀️`,
          });
        },
        onError: () => {
          toast({
            title: "Failed to send GM",
            description: "Something went wrong. Please try again.",
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarImage src={profileImage} alt={displayName} />
        <AvatarFallback>{displayName[0]?.toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{displayName}</p>
        <p className="text-xs text-muted-foreground truncate">
          {reply.content}
        </p>
      </div>
      {hasRepliedBack ? (
        <span className="text-xs text-green-600 dark:text-green-400 shrink-0">
          GMed back
        </span>
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={handleGMBack}
          disabled={isPending}
          className="shrink-0"
        >
          {isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            "GM back"
          )}
        </Button>
      )}
    </div>
  );
}
